import { useCallback, useRef, useState } from "react";
import {
  getMergedCachedData,
  setCachedDocument,
} from "../utils/documentCache";

/**
 * useDocumentCache
 *
 * Thin orchestration over `documentCache` that keeps the cache lifecycle out of
 * the content container. It reads the cached payload once on mount, tracks a UI
 * lock for the cached preview, and exposes a write-back helper.
 *
 * @param {string} id       Document id.
 * @param {boolean} enabled  Caching applies ONLY to normal documents
 *                           (never templates / read-only / conflict / offline).
 */
export function useDocumentCache(id, enabled = true) {
  // Read the cached merged payload ONCE on mount (lazy initializer).
  const [cached] = useState(() =>
    enabled && id ? getMergedCachedData(id) : null
  );

  // Locks edits while the cached preview is on screen, before fresh data lands.
  const [isUiLocked, setIsUiLocked] = useState(false);

  // Guards a single instant paint from the cache.
  const cacheRenderedRef = useRef(false);

  const lockUi = useCallback(() => setIsUiLocked(true), []);
  const unlockUi = useCallback(() => setIsUiLocked(false), []);

  const commitCache = useCallback(
    (documentData, documentInfo) => {
      if (!enabled || !id) return;
      setCachedDocument(id, { documentData, documentInfo });
    },
    [enabled, id]
  );

  return {
    cached,
    isUiLocked,
    lockUi,
    unlockUi,
    cacheRenderedRef,
    commitCache,
  };
}

export default useDocumentCache;
